import { useState, useEffect } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Server, KeyRound, Globe, Plug, FileCode, Trash2, CheckCircle2, Loader2, Upload, Users, ShieldCheck, Shield, Ban, UserCheck } from "lucide-react";

function UsersAdmin() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get("/admin/users")).data,
  });
  const [busyId, setBusyId] = useState(null);

  const setRole = async (u, role) => {
    setBusyId(u.user_id);
    try {
      await api.post(`/admin/users/${u.user_id}/role`, { role });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success(role === "admin" ? `${u.name} est maintenant admin` : `${u.name} repasse joueur`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    } finally {
      setBusyId(null);
    }
  };

  const setBan = async (u, ban) => {
    if (ban && !window.confirm(`Bannir ${u.ign || u.name} ? Ses sessions seront fermées et il ne pourra plus se connecter.`)) return;
    setBusyId(u.user_id);
    try {
      await api.post(`/admin/users/${u.user_id}/${ban ? "ban" : "unban"}`);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success(ban ? `${u.ign || u.name} a été banni` : `${u.ign || u.name} a été débanni`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-5" data-testid="users-admin">
      <div className="flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-primary" />
        <h3 className="font-head font-semibold">Joueurs & rôles</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Promeus un joueur en <span className="text-primary">admin</span> (accès RCON + paramètres), rétrograde-le, ou <span className="text-red-400">bannis</span> un compte (déconnexion immédiate + connexion bloquée). Aucun accès à la base nécessaire.
      </p>
      {isLoading ? (
        <div className="flex justify-center py-6 text-primary"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : (
        <div className="divide-y divide-border max-h-80 overflow-y-auto scrollbar-thin -mx-2">
          {users.map((u) => {
            const isAdmin = u.role === "admin";
            const isMe = u.user_id === me?.user_id;
            const banned = !!u.banned;
            return (
              <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className={`flex items-center gap-3 px-2 py-2.5 ${banned ? "opacity-60" : ""}`}>
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                    {(u.ign || u.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">
                    {u.ign || u.name}{isMe && <span className="text-primary"> (toi)</span>}
                    {banned && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wider text-red-400">· Banni</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {u.ign && u.name !== u.ign ? `${u.name} · ` : ""}{u.email} · {u.defeated} boss
                  </div>
                </div>
                <span className={`hidden sm:inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shrink-0 ${isAdmin ? "text-primary bg-primary/10" : "text-muted-foreground bg-black/30"}`}>
                  {isAdmin ? <ShieldCheck className="w-3 h-3" /> : <Shield className="w-3 h-3" />} {isAdmin ? "Admin" : "Joueur"}
                </span>
                {!isMe && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      data-testid={`toggle-role-${u.user_id}`}
                      onClick={() => setRole(u, isAdmin ? "user" : "admin")}
                      disabled={busyId === u.user_id}
                      className={`text-xs font-semibold rounded-md px-2.5 py-1.5 border transition-colors disabled:opacity-40 ${isAdmin ? "border-border text-muted-foreground hover:text-foreground" : "border-primary/40 text-primary hover:bg-primary/10"}`}
                    >
                      {busyId === u.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isAdmin ? "Rétrograder" : "Promouvoir"}
                    </button>
                    {!isAdmin && (
                      <button
                        data-testid={`toggle-ban-${u.user_id}`}
                        onClick={() => setBan(u, !banned)}
                        disabled={busyId === u.user_id}
                        title={banned ? "Débannir" : "Bannir"}
                        className={`inline-flex items-center justify-center rounded-md p-1.5 border transition-colors disabled:opacity-40 ${banned ? "border-secondary/40 text-secondary hover:bg-secondary/10" : "border-red-500/40 text-red-400 hover:bg-red-500/10"}`}
                        style={banned ? { color: "#22C55E", borderColor: "#22C55E66" } : {}}
                      >
                        {banned ? <UserCheck className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "Mon Serveur Palworld", host: "", rcon_port: 25575, password: "" });
  const [configured, setConfigured] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [ini, setIni] = useState("");

  useEffect(() => {
    api.get("/config").then(({ data }) => {
      if (data.configured) {
        setConfigured(true);
        setForm((f) => ({ ...f, name: data.name || f.name, host: data.host, rcon_port: data.rcon_port }));
      }
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyParsed = (text, label) => {
    const opt = text.match(/OptionSettings\s*=\s*\(([^]*)\)/i);
    const body = opt ? opt[1] : text;
    const port = body.match(/RCONPort\s*=\s*(\d+)/i);
    const pass = body.match(/AdminPassword\s*=\s*"?([^",\r\n]*)"?/i);
    const ip = body.match(/PublicIP\s*=\s*"?([^",\r\n]*)"?/i);
    const server = body.match(/ServerName\s*=\s*"?([^",\r\n]*)"?/i);
    const enabled = body.match(/RCONEnabled\s*=\s*(True|False)/i);

    const parsed = {};
    if (port) parsed.rcon_port = parseInt(port[1]);
    if (pass && pass[1]) parsed.password = pass[1];
    if (ip && ip[1]) parsed.host = ip[1];
    if (server && server[1]) parsed.name = server[1];
    const found = Object.keys(parsed).length;

    if (found) setForm((f) => ({ ...f, ...parsed }));

    if (found) {
      toast.success(`${label} — ${found} paramètre(s) importé(s)`);
      if (!(ip && ip[1])) toast.info("PublicIP absente : renseigne l'IP publique de ton serveur à la main");
    } else {
      toast.error("Aucun paramètre RCON trouvé dans le contenu");
    }
    if (enabled && enabled[1].toLowerCase() === "false") {
      toast.warning("RCONEnabled=False — active RCON sur ton serveur pour que ça fonctionne");
    }
    return found;
  };

  const parseIni = () => applyParsed(ini, "Contenu analysé");

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target.result || "");
      setIni(text);
      applyParsed(text, `Fichier « ${file.name} » lu`);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const save = async () => {
    if (!form.host.trim() || !form.password.trim()) {
      toast.error("IP/hôte et mot de passe RCON sont requis");
      return;
    }
    setSaving(true);
    try {
      await api.post("/config", form);
      setConfigured(true);
      qc.invalidateQueries({ queryKey: ["status"] });
      toast.success("Configuration enregistrée");
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    if (!form.host.trim() || !form.password.trim()) {
      toast.error("Renseigne l'IP/hôte et le mot de passe RCON avant de tester");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/server/test", form);
      if (data.online) toast.success(`Connecté ! Version ${data.version || "?"} · ${data.player_count ?? 0} joueur(s)`);
      else toast.error(data.error || "Serveur injoignable");
    } catch {
      toast.error("Échec du test de connexion");
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    await api.delete("/config");
    setConfigured(false);
    setForm({ name: "Mon Serveur Palworld", host: "", rcon_port: 25575, password: "" });
    qc.invalidateQueries({ queryKey: ["status"] });
    toast.success("Configuration supprimée");
  };

  return (
    <div className="fade-up max-w-2xl space-y-6">
      <div>
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-primary/80">Panneau admin</div>
        <h1 className="font-head text-3xl sm:text-4xl font-bold tracking-tight">Administration</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Gère la connexion RCON à ton serveur Palworld 1.0, les rôles des joueurs et les bannissements.
        </p>
      </div>

      {configured && (
        <div className="flex items-center gap-2 rounded-md border border-secondary/30 bg-secondary/5 px-4 py-3 text-sm" style={{ color: "#22C55E" }}>
          <CheckCircle2 className="w-4 h-4" /> Serveur configuré
        </div>
      )}

      {/* Import ini */}
      <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-5">
        <div className="flex items-center gap-2 mb-2">
          <FileCode className="w-4 h-4 text-primary" />
          <h3 className="font-head font-semibold">Importer depuis PalWorldSettings.ini</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Téléverse ton fichier <code className="text-primary">PalWorldSettings.ini</code> (téléchargé depuis ton FTP) ou colle son contenu — le port RCON, le mot de passe admin et le nom du serveur seront détectés automatiquement.
        </p>

        <label
          data-testid="ini-upload-label"
          className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-primary/40 bg-primary/5 px-4 py-6 cursor-pointer hover:bg-primary/10 transition-colors mb-3"
        >
          <Upload className="w-6 h-6 text-primary" />
          <span className="text-sm font-semibold text-primary">Choisir le fichier PalWorldSettings.ini</span>
          <span className="text-[11px] text-muted-foreground">Fichiers .ini / .txt — lu directement dans ton navigateur</span>
          <input
            data-testid="ini-file-input"
            type="file"
            accept=".ini,.txt,text/plain"
            onChange={handleFile}
            className="hidden"
          />
        </label>

        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 text-center">— ou coller le contenu —</div>

        <textarea
          data-testid="ini-textarea"
          value={ini}
          onChange={(e) => setIni(e.target.value)}
          rows={4}
          placeholder="OptionSettings=(...RCONEnabled=True,RCONPort=25575,AdminPassword=&quot;...&quot;,PublicIP=&quot;...&quot;)"
          className="w-full rounded-md bg-black/40 border border-border px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-primary focus:outline-none scrollbar-thin"
        />
        <button
          data-testid="parse-ini-btn"
          onClick={parseIni}
          disabled={!ini.trim()}
          className="mt-3 inline-flex items-center gap-2 rounded-md border border-primary/40 text-primary px-3 py-2 text-sm font-semibold hover:bg-primary/10 transition-colors disabled:opacity-40"
        >
          <FileCode className="w-4 h-4" /> Analyser le fichier
        </button>
      </div>

      {/* Manual form */}
      <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-5 space-y-4">
        <Field icon={Server} label="Nom du serveur">
          <input data-testid="cfg-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="cfg-input" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <Field icon={Globe} label="IP / Hôte">
              <input data-testid="cfg-host" value={form.host} onChange={(e) => set("host", e.target.value)} placeholder="123.45.67.89" className="cfg-input" />
            </Field>
          </div>
          <Field icon={Plug} label="Port RCON">
            <input data-testid="cfg-port" type="number" value={form.rcon_port} onChange={(e) => set("rcon_port", parseInt(e.target.value) || 0)} className="cfg-input" />
          </Field>
        </div>
        <Field icon={KeyRound} label="Mot de passe admin (RCON)">
          <input data-testid="cfg-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="••••••••" className="cfg-input" />
        </Field>

        <div className="flex flex-wrap gap-3 pt-2">
          <button
            data-testid="save-config-btn"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/85 transition-colors disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Enregistrer
          </button>
          <button
            data-testid="test-config-btn"
            onClick={test}
            disabled={testing}
            className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold hover:border-primary/50 transition-colors disabled:opacity-40"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />} Tester la connexion
          </button>
          {configured && (
            <button
              data-testid="delete-config-btn"
              onClick={remove}
              className="inline-flex items-center gap-2 rounded-md border border-destructive/40 text-destructive px-4 py-2 text-sm font-semibold hover:bg-destructive/10 transition-colors ml-auto"
            >
              <Trash2 className="w-4 h-4" /> Supprimer
            </button>
          )}
        </div>
      </div>

      <UsersAdmin />

      <p className="text-xs text-muted-foreground">
        Astuce : dans <code className="text-primary">PalWorldSettings.ini</code>, assurez-vous d'avoir
        <code className="text-primary"> RCONEnabled=True</code> et un <code className="text-primary">RCONPort</code> ouvert dans votre pare-feu.
      </p>
    </div>
  );
}

const Field = ({ icon: Icon, label, children }) => (
  <div>
    <label className="flex items-center gap-1.5 text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1.5">
      <Icon className="w-3.5 h-3.5" /> {label}
    </label>
    {children}
  </div>
);
